import { Bind, registerComponent } from "@eusoft/webapp-core";
import { handleError } from "@eusoft/webapp-framework/helpers/Error";
import { MessageBoxButton, userInteraction } from "@eusoft/webapp-framework/services/UserInteraction";
import { forModel } from "@eusoft/webapp-jsx";
import { Action, CheckBox, Content, contentInfo, formatCurrency, formatText, MaterialIcon, ObjectEditor, validEmail, type IAction, type ViewNode } from "@eusoft/webapp-ui";
import { paymentService, type IPaymentCard } from "../../abstraction/IPaymentService";
import { navigatePage, printAsync } from "../../actions/Common";
import { writeCardWalletAsync } from "../../actions/PaymentCard";
import { PaymentCardStatus, PaymentCardType, SalePointType } from "../../entities/Commands";
import { formatMarkDown } from "../../helpers/Format";
import { Authorize } from "../../services/Authorize";
import { context } from "../../services/Context";
import { executeAsync } from "../../services/DataService";
import { isNativeApp } from "../../services/NativePayment";
import { WithContext } from "../../services/WithContext";

interface ICardInfo {
    email: string;
    fullName: string;
    phoneNumber: string;
}

interface IState {
    printReceipt: boolean;
}

export class InitCardPage extends Content {

    _state: IState;
    _completeActions: IAction[];

    constructor() {

        super();

        this.cardInfo = {} as ICardInfo;

        this._completeActions = [{
            name: "recharge",
            text: "recharge-card",
            executeAsync: async () => {
                navigatePage("recharge-card", {
                    cardCode: this.lastCard.code
                });
            }
        }, {
            name: "init-new",
            text: "init-new-card",
            executeAsync: async () => this.initNewCard()

        }];

        this.init(InitCardPage, {
            title: "init-card",
            style: ["panel"],

            body: forModel(this, m => <>
                <ObjectEditor<ICardInfo>
                    validationMode="onInputChange"
                    style={["vertical"]}
                    inputField={{ style: ["filled"] }}
                    value={m.cardInfo}
                    name="card-info"
                    isValid={m.isValid}
                    commitMode="auto-inplace"
                    builder={bld => <>

                        {bld.text(m => m.email, {
                            validators: [validEmail],
                            editor: {
                                type: "email"
                            }
                        })}
                        {bld.text(m => m.fullName, {
                            validators: [],
                            editor: {
                            }
                        })}
                        {bld.text(m => m.phoneNumber, {
                            validators: [],
                            editor: {
                                type: "tel"
                            }
                        })}

                    </>}
                />
                {(isNativeApp() || true) && <CheckBox value={Bind.twoWays(m.printReceipt)}>
                    {formatText("print-receipt")}
                </CheckBox>}
                <Action onExecuteAsync={() => m.initCardAsync()}>
                    initialize
                </Action>
            </>)
        });

        this.loadPageState();
    }

    protected loadPageState() {

        const json = localStorage.getItem(this.name);

        this._state = json ? JSON.parse(json) : {} as IState;

        if (this._state.printReceipt !== undefined)
            this.printReceipt = this._state.printReceipt;
    }

    protected savePageState(state: Partial<IState>) {

        if (state)
            Object.assign(this._state, state);

        localStorage.setItem(this.name, JSON.stringify(this._state));
    }

    initNewCard() {

        this.cardInfo.email = "";
        this.cardInfo.fullName = "";
        this.cardInfo.phoneNumber = "";
        this.completeMessage = null;
        this.lastCard = null;
    }

    protected async initCardAsync() {


        if (!this.isValid)
            return;

        this.savePageState({ printReceipt: this.printReceipt });
    
        const card = await handleError(() => paymentService.readCardAsync({
            message: formatText("msg-wait-for-card-verify") as string,
        }));

        if (!card)
            return;

        const info = await executeAsync("GetCardInfo", {
            cardCode: card.code,
            checkToken: null
        })

        if (info.cardStatus == PaymentCardStatus.Blocked) {

            const resp = await userInteraction.messageBoxAsync(formatText("msg-card-blocked-warning"),
                "error", MessageBoxButton.YesNo);

            if (resp == MessageBoxButton.No)
                return;
        }

        let action: "reset" | "refund" | "cancel";

        if (info.saleCircuit?.id != context?.saleCircuit.id) {
            action = await userInteraction.messageBoxAsync(formatMarkDown("err-invalid-card-sale-circuit",
                formatCurrency(info.balance)), "warning", {
                "reset": {},
                "cancel": {},
            });
        }

        else if (info.balance > 0) {
            action = await userInteraction.messageBoxAsync(formatMarkDown("msg-card-has-balance",
                formatCurrency(info.balance)), "warning", {
                "reset": {},
                "refund": { label: "refund-action" },
                "cancel": {},
            });
        }

        if (action == "cancel")
            return;

        if (action == "refund") {
            navigatePage("refund-card", { cardCode: card.code });
            return;
        }

        if (action == "reset") {

            const resetRes = await executeAsync("ResetCard", {
                cardId: info.cardId,
                salePointId: context.salePoint.id,
            });

            if (!resetRes)
                return;

            info.walletId = null;
            info.balance = 0;
        }

        let walletId = info.walletId;

        if (walletId && card.uri && card.uri.indexOf(walletId) != -1 && !info.hasTransactions) {
            await userInteraction.messageAsync(formatText("err-card-already-init"), "warning", 3000);
            return;
        }

        if (!walletId) {
            walletId = await executeAsync("InitCard", {
                cardCode: card.code,
                salePointId: context.salePoint?.id,
                cardType: card.type,
                customerEmail: this.cardInfo.email,
                customerFullName: this.cardInfo.fullName,
                customerPhoneNumber: this.cardInfo.phoneNumber,
            })

            if (!walletId)
                return;
        }

        if (card.type == PaymentCardType.Nfc) {

            const writeRes = await writeCardWalletAsync(walletId);
            console.log("Write card: ", writeRes);
        }

        this.lastCard = card;

        if (this.printReceipt && isNativeApp()) {
            await printAsync("InitCard", {
                walletId,
                cardCode: card.code
            })
        }

        await userInteraction.fullMessageAsync(
            formatText("msg-card-init-complete"), "success",
            this._completeActions);
    }

    lastCard: IPaymentCard;

    cardInfo: ICardInfo;

    completeMessage: ViewNode;

    isValid: boolean;

    printReceipt: boolean;

    static override info = contentInfo({
        name: "init-card",
        route: "/cards/init",
        icon: <MaterialIcon style="green" name="add_task" />,
        features:[
            Authorize(a => a.businessAccount().resource("PaymentCard", "write")),
            WithContext("sale-point", SalePointType.Charge)
        ],
        factory: () => new InitCardPage()
    });
}

registerComponent(InitCardPage, "InitCardPage");